/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable sonarjs/no-duplicate-string */
import { html, unsafeCSS, PropertyValues, TemplateResult, nothing } from 'lit';
import { state, query, queryAll } from 'lit/decorators.js';
import { until } from 'lit/directives/until.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { choose } from 'lit/directives/choose.js';
import { ScopedRegistryHost } from '@lit-labs/scoped-registry-mixin';
import { ContextConsumer } from '@lit/context';
import { AuthnTransaction } from '@okta/okta-auth-js';
import { LeafElement } from '@cigna/dcws_leaf-web-components/components/leaf-element.js';
import { LeafButton } from '@cigna/dcws_leaf-web-components/components/button/button.component.js';
import { LeafInput } from '@cigna/dcws_leaf-web-components/components/input/input.component.js';
import { LeafLabel } from '@cigna/dcws_leaf-web-components/components/label/label.component.js';
import { LeafSpinner } from '@cigna/dcws_leaf-web-components/components/spinner/spinner.component.js';
import { LeafValidationMessage } from '@cigna/dcws_leaf-web-components/components/validation-message/validation-message.component.js';
import { LeafAlert } from '@cigna/dcws_leaf-web-components/components/alert/alert.component.js';
import { LeafIcon } from '@cigna/dcws_leaf-web-components/components/icon/icon.component.js';
import { CHCPhoneNumber } from '@cigna/dcws_leaf-web-components/chc-components/phone-number/phone-number.component.js';
import { browserIsSafari } from '@cigna/dcws_leaf-web-components/utilities/browser-detection.js';
import { isMobile } from '@cigna/dcws_leaf-web-components/utilities/is-mobile.js';
import { I18nMixin } from '../../utils/i18n-mixin.js';
import { endpointsContext } from '../contexts.js';
import { LoginService } from './service.js';
import styles from './login-banner.scss?inline';
import type { EnvironmentConfig } from '../../environments/interfaces.js';

interface OAuthErrorResponse {
  errorCode: string;
  errorSummary: string;
  errorLink: string;
  errorId: string;
  errorCauses: Array<string>;
}

interface CHCAuthTransaction extends AuthnTransaction {
  data: {
    stateToken?: string;
  };
}

export class CHCLoginBanner extends ScopedRegistryHost(I18nMixin(LeafElement)) {
  protected override ns = 'login-banner';

  static elementDefinitions = {
    'leaf-button': LeafButton,
    'leaf-input': LeafInput,
    'leaf-label': LeafLabel,
    'leaf-spinner': LeafSpinner,
    'leaf-validation-message': LeafValidationMessage,
    'leaf-icon': LeafIcon,
    'leaf-alert': LeafAlert,
    'chc-phone-number': CHCPhoneNumber,
  };
  static override styles = unsafeCSS(styles);

  @queryAll('leaf-input')
  _ciInput!: HTMLElement[];

  @query('leaf-button')
  _ciButton!: HTMLElement;

  @query('form')
  _ciForm!: HTMLFormElement;

  @state()
  protected formDataEntries: { [key: string]: any } = {};

  @state()
  protected get hasFormData() {
    return Object.keys(this.formDataEntries).length !== 0;
  }

  // @consume({ context: loginServiceContext, subscribe: true })
  @state()
  private loginService!: LoginService;

  // @consume({ context: myCignaContext, subscribe: true })
  @state()
  private myCignaConfig!: EnvironmentConfig['endpoints']['myCignaLogin'];

  constructor() {
    super();
    new ContextConsumer(this, {
      context: endpointsContext,
      callback: (value) => {
        this.myCignaConfig = value.myCignaLogin;
        this.loginService = new LoginService(value.myCignaLogin);
      },
    });
  }

  override connectedCallback(): void {
    super.connectedCallback();

    if (browserIsSafari()) {
      window.onpageshow = (event) => {
        if (event.persisted) {
          window.location.reload();
        }
      };
    }
  }

  override async firstUpdated(changedProperties: PropertyValues): Promise<void> {
    super.firstUpdated(changedProperties);
    await this.updateComplete;

    if (isMobile('lg') && this._ciForm) {
      this.removeTabbing();
    }
  }

  private removeTabbing(): void {
    this._ciInput.forEach((element) => {
      element.querySelector('input')?.setAttribute('tabindex', `-1`);
      element.querySelectorAll('a').forEach((link) => link?.setAttribute('tabindex', `-1`));
    });
    this._ciButton.shadowRoot?.querySelector('button')?.setAttribute('tabindex', `-1`);
  }

  private loginMFA(transaction: CHCAuthTransaction) {
    if (browserIsSafari()) {
      window.location.assign(`${this.myCignaConfig?.guest}/stepup?token=${transaction.data.stateToken}`);
    } else {
      window.open(`${this.myCignaConfig?.guest}/stepup?token=${transaction.data.stateToken}`, '_blank');
    }
  }

  private loginSuccess() {
    if (browserIsSafari()) {
      window.location.assign(`${this.myCignaConfig?.consumer}`);
    } else {
      window.open(`${this.myCignaConfig?.consumer}`, '_blank');
    }
  }

  getFormDataEntries = (formData: FormData): { [key: string]: any } => {
    const values: { [key: string]: any } = {};
    for (const [key, value] of formData.entries()) {
      values[key] = value;
    }
    return values;
  };

  validateForm(form: HTMLFormElement) {
    const formData = new FormData(form);
    return new Promise<FormData>((resolve) => {
      if (!form.checkValidity()) {
        form.reportValidity();
      } else {
        resolve(formData);
      }
    });
  }

  protected renderForm(): TemplateResult {
    return html`
      ${this.hasFormData ? this.renderAuthMessaging() : nothing}
      <form class="chcapp-login-banner__form">
        <div class="chcapp-login-banner__user-pass-wrapper">
          <div class="chcapp-login-banner__input-username">
            <leaf-label for="chcapp-login-banner__input-username">${this.i18n?.t('login-banner:username')}</leaf-label>
            <leaf-input autocomplete="on" id="chcapp-login-banner__input-username" name="username" required type="text">
            </leaf-input>
          </div>
          <div class="chcapp-login-banner__input-password">
            <leaf-label for="chcapp-login-banner__input-password">${this.i18n?.t('login-banner:password')}</leaf-label>
            <leaf-input
              autocomplete="on"
              name="password"
              required
              type="password"
              id="chcapp-login-banner__input-password"
            >
              <leaf-button variant="tertiary" slot="password-show"
                ><leaf-icon
                  slot="icon-before"
                  label="${this.i18n?.t('login-banner:show')} ${this.i18n?.t('login-banner:password')}"
                  name="visibility"
                ></leaf-icon
              ></leaf-button>
              <leaf-button variant="tertiary" slot="password-hide"
                ><leaf-icon
                  slot="icon-before"
                  label="${this.i18n?.t('login-banner:hide')} ${this.i18n?.t('login-banner:password')}"
                  name="visibility-off"
                ></leaf-icon
              ></leaf-button>
            </leaf-input>
          </div>
          ${this.renderForgotUserPass()}
        </div>
        ${this.renderForgotUserPass(true)}
        <leaf-button
          type="button"
          class="chcapp-login-banner__submit-button"
          @click=${(event: { target: EventTarget }) => {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            const form = (event.target as HTMLElement).closest('form')!;

            this.validateForm(form)
              .then(async (detail) => {
                const data = detail;
                return this.getFormDataEntries(data);
              })
              .then((result) => {
                this.formDataEntries = result;
              });
          }}
          >${this.i18n?.t('login-banner:login')}</leaf-button
        >
      </form>
    `;
  }

  protected renderForgotUserPass(isMobile = false) {
    return html`<div
      class="chcapp-login-banner__forgot-wrapper ${isMobile ? 'chcapp-login-banner__forgot-wrapper--mobile' : ''}"
    >
      ${this.i18n?.t('login-banner:forgot')}
      <a
        slot="link"
        href="${ifDefined(this.myCignaConfig?.forgotID)}"
        target="_blank"
        variant="standalone"
        class="chcapp-login-banner__user-pass-link"
        >${this.i18n?.t('login-banner:username')}</a
      >
      ${this.i18n?.t('login-banner:or')}
      <a
        slot="link"
        href="${ifDefined(this.myCignaConfig?.forgotPassword)}"
        target="_blank"
        variant="standalone"
        class="chcapp-login-banner__user-pass-link"
        >${this.i18n?.t('login-banner:password')}</a
      >${this.i18n?.t('login-banner:question-mark')}
    </div>`;
  }

  protected renderAuthMessaging() {
    return html`<div class="chcapp-login-banner__auth-messaging">
      ${until(
        this.loginService
          .login(this.formDataEntries['username'], this.formDataEntries['password'])
          .then((transaction) => {
            return html`
              ${choose(
                transaction.status,
                [
                  [
                    'MFA_REQUIRED',
                    () => html`
                      ${this.loginMFA(transaction as CHCAuthTransaction)}
                      <leaf-alert variant="inline" type="success">
                        <p>${this.i18n?.t('login-banner:login_success')}</p>
                      </leaf-alert>
                    `,
                  ],
                ],
                () => html`
                  ${this.loginSuccess()}
                  <leaf-alert variant="inline" type="success">
                    <p>${this.i18n?.t('login-banner:login_success')}</p>
                  </leaf-alert>
                `,
              )}
            `;
          })
          .catch((error) => {
            if (error.xhr && JSON.stringify(error.xhr.status) === '403') {
              const errorId = JSON.stringify(error.xhr.responseJSON.errorId) as OAuthErrorResponse['errorId'];
              return html`
                <leaf-alert variant="inline" type="error" role="alert" aria-live="assertive">
                  <p>
                    ${this.i18n?.t('login-banner:rejected_error_1')}<chc-phone-number
                      number="8008532713"
                      separator="-"
                      parentheses
                      campaign-opt-out
                    ></chc-phone-number
                    >&nbsp;${this.i18n?.t('login-banner:rejected_error_2')}${JSON.parse(errorId)}.
                  </p>
                </leaf-alert>
              `;
            } else {
              return html`
                ${choose(
                  error.name,
                  [
                    [
                      'AuthApiError',
                      () => html`
                        <leaf-alert variant="inline" type="error" role="alert" aria-live="assertive">
                          <p>${this.i18n?.t('login-banner:login_error')}</p>
                        </leaf-alert>
                      `,
                    ],
                    [
                      'OAuthError',
                      () => html`
                        <leaf-alert variant="inline" type="error" role="alert" aria-live="assertive">
                          <p>
                            ${this.i18n?.t('login-banner:login_locked_out')}
                            <chc-phone-number
                              number="8887367499"
                              separator="-"
                              parentheses
                              campaign-opt-out
                            ></chc-phone-number
                            >&nbsp;${this.i18n?.t('login-banner:login_locked_out_tty')}.
                          </p>
                        </leaf-alert>
                      `,
                    ],
                  ],
                  () => html`
                    <leaf-alert variant="inline" type="info">
                      <p>${this.i18n?.t('login-banner:server_error')}</p>
                    </leaf-alert>
                  `,
                )}
              `;
            }
          }),
        html`
          <leaf-spinner size="small" class="chcapp-login-banner__auth-spinner"
            ><span>${this.i18n?.t('login-banner:authenticate')}</span></leaf-spinner
          >
        `,
      )}
    </div>`;
  }

  override render() {
    const loadingOrError = this.renderLoadingOrError();
    return html`${loadingOrError
      ? loadingOrError
      : // eslint-disable-next-line sonarjs/no-nested-template-literals
        html` <div class="chcapp-login-banner">${this.renderForm()}</div> `}`;
  }
}
